import { useState } from "react";
import {
  Hash,
  Inbox,
  Pencil,
  UserX,
  UserCheck,
  Trash2,
  Package,
  Calendar,
  MapPin,
  UserRound,
  Boxes,
  Plus,
  Tag,
  RotateCcw,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import swal from "../../utils/swal";
import { Form, Input, DatePicker, Drawer } from "../../components/ui";
import { UPLOADS_URL } from "../../config/constants";
import {
  useGetCategories,
  useToggleInventoryStatus,
  useIssueStock,
  useRemoveIssuance,
} from "../../query/inventoryQuery";
import { useGetAllTeachers } from "../../query/teacherQuery";
import { useGetAllClasses } from "../../query/classQuery";
import {
  ASSIGNED_TO_TYPES,
  availableQuantity,
  totalIssued,
  assigneeTypeLabel,
  issuanceAssigneeLabel,
} from "./inventoryHelpers";
import { DetailCoverHero } from "../../components/DetailCoverHero";
import "../teachers/teacherDetails.css";
import "./inventory.css";

const LESSON_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];

function normalizeGallery(gallery) {
  if (!gallery) return [];
  if (Array.isArray(gallery)) return gallery.filter(Boolean);
  return [];
}

function formatDate(value) {
  return value ? dayjs(value).format("MMMM D, YYYY") : "—";
}

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

export default function InventoryProfile({ item, onDelete }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [issueOpen, setIssueOpen] = useState(false);
  const { mutate: toggleStatus, isPending: isUpdatingStatus } = useToggleInventoryStatus();
  const { mutate: issueStock, isPending: isIssuing } = useIssueStock();
  const { mutate: removeIssuance } = useRemoveIssuance();
  const { data: categories = [] } = useGetCategories();
  const { data: teachersData } = useGetAllTeachers({ page: 1, limit: 200 });
  const { data: classesData } = useGetAllClasses({ page: 1, limit: 200 });
  const teachers = teachersData?.docs || [];
  const classrooms = classesData?.docs || [];
  const assignedToType = Form.useWatch("assignedToType", form) || "TEACHER";
  const [activeIndex, setActiveIndex] = useState(0);

  const gallery = normalizeGallery(item?.gallery);
  const coverImage = gallery[activeIndex] || gallery[0];
  const isActive = item?.status === "ACTIVE";
  const statusText = isActive ? "Active" : "Inactive";
  const inStock = Number(item?.quantity) || 0;
  const issued = totalIssued(item);
  const available = availableQuantity(item);
  const issuances = item?.issuances || [];

  const categoryId = item?.category?._id || item?.category;
  const categoryTitle =
    item?.category?.title ||
    categories.find((cat) => String(cat._id) === String(categoryId))?.title ||
    "Uncategorized";

  const subtitle = [categoryTitle, item?.sku ? `SKU ${item.sku}` : null].filter(Boolean).join(" · ");

  const openIssueDrawer = () => {
    form.resetFields();
    form.setFieldsValue({ assignedToType: "TEACHER", issuedDate: dayjs() });
    setIssueOpen(true);
  };

  const closeIssueDrawer = () => {
    setIssueOpen(false);
    form.resetFields();
  };

  const handleIssue = () => {
    form.validateFields().then((values) => {
      const qty = Number(values.quantity) || 0;
      if (qty > available) {
        swal("Oops!", `Only ${available} units available to issue`, "error");
        return;
      }

      let assignedToName = values.assignedToName || "";
      if (values.assignedToType === "TEACHER" && values.assignedToId) {
        const teacher = teachers.find((t) => String(t._id) === String(values.assignedToId));
        assignedToName = teacher
          ? `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim()
          : assignedToName;
      }
      if (values.assignedToType === "CLASSROOM" && values.assignedToId) {
        const classroom = classrooms.find((c) => String(c._id) === String(values.assignedToId));
        assignedToName = classroom?.title || classroom?.name || classroom?.sectionId || assignedToName;
      }

      issueStock(
        {
          id: item._id,
          quantity: qty,
          assignedToType: values.assignedToType,
          assignedToId: values.assignedToId || "",
          assignedToName,
          issuedDate: asDateString(values.issuedDate),
          notes: values.notes || "",
        },
        {
          onSuccess: () => {
            swal("Success", "Stock issued successfully", "success");
            closeIssueDrawer();
          },
          onError: (error) => swal("Error", error?.message || "Failed to issue stock", "error"),
        }
      );
    });
  };

  const handleReturn = (row) => {
    swal({
      title: "Return to stock?",
      text: `Return ${row.quantity} units from ${issuanceAssigneeLabel(row)}?`,
      icon: "warning",
      buttons: ["Cancel", "Return"],
    }).then((isConfirm) => {
      if (!isConfirm) return;
      removeIssuance(
        { id: item._id, issuanceId: row._id },
        {
          onSuccess: () => swal("Success", "Stock returned to inventory", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to return stock", "error"),
        }
      );
    });
  };

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this item?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      toggleStatus(item._id, {
        onSuccess: () => swal("Success", "Status updated successfully", "success"),
        onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
      });
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
        <article className="td-card">
          <DetailCoverHero
            coverKey={item?._id}
            backLabel="Back to inventory"
            onBack={() => navigate("/inventory")}
          />

          <div className="td-body">
            <div className="td-identity">
              {coverImage ? (
                <img
                  className="td-avatar inv-avatar"
                  src={`${UPLOADS_URL}/${coverImage}`}
                  alt={item?.title || "Inventory item"}
                />
              ) : (
                <span className="td-avatar inv-avatar">
                  <Package size={36} strokeWidth={1.6} />
                </span>
              )}
              <div className="td-identity-row">
                <h2>{item?.title || "Inventory Item"}</h2>
                <span className={`td-status is-${(item?.status || "inactive").toLowerCase()}`}>
                  {statusText}
                </span>
              </div>
              <p>{subtitle || "Inventory"}</p>
            </div>

            <div className="td-chips">
              <span>
                <i className="is-orange">
                  <Boxes size={14} />
                </i>
                {inStock} total · {available} available
              </span>
              <span>
                <i className="is-orange">
                  <UserRound size={14} />
                </i>
                {issued} issued out
              </span>
              <span>
                <i className="is-orange">
                  <Tag size={14} />
                </i>
                {categoryTitle}
              </span>
              {item?.storageLocation ? (
                <span>
                  <i className="is-orange">
                    <MapPin size={14} />
                  </i>
                  {item.storageLocation}
                </span>
              ) : null}
            </div>

            <section className="td-section">
              <h3>About:</h3>
              {item?.description ? (
                <p>{item.description}</p>
              ) : (
                <div className="td-empty">
                  <Inbox size={22} />
                  <span>No description provided</span>
                </div>
              )}
            </section>

            <section className="td-section">
              <h3>Details:</h3>
              <ul>
                <li>
                  <strong>Storage location</strong>
                  <em>{item?.storageLocation || "Not set"}</em>
                </li>
                <li>
                  <strong>Supplier</strong>
                  <em>{item?.manufacturer || "Not set"}</em>
                </li>
                <li>
                  <strong>Date acquired</strong>
                  <em>{formatDate(item?.purchaseDate)}</em>
                </li>
                <li>
                  <strong>Last stock check</strong>
                  <em>{formatDate(item?.lastAuditDate)}</em>
                </li>
              </ul>
            </section>

            {gallery.length > 1 ? (
              <section className="td-section">
                <h3>Photos:</h3>
                <div className="inv-photo-strip">
                  {gallery.map((filename, index) => (
                    <button
                      key={filename}
                      type="button"
                      className={`inv-photo-thumb${index === activeIndex ? " is-active" : ""}`}
                      onClick={() => setActiveIndex(index)}
                      aria-label={`View photo ${index + 1}`}
                    >
                      <img src={`${UPLOADS_URL}/${filename}`} alt="" />
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {item?.notes ? (
              <section className="td-section">
                <h3>Notes:</h3>
                <p>{item.notes}</p>
              </section>
            ) : null}
          </div>
        </article>

        <div className="td-actions">
          <button
            type="button"
            className="td-action is-primary"
            onClick={() => navigate(`/inventory/edit/${item._id}`)}
          >
            <Pencil size={15} />
            Edit details
          </button>
          {available > 0 ? (
            <button type="button" className="td-action" onClick={openIssueDrawer}>
              <Plus size={15} />
              Issue stock
            </button>
          ) : null}
          <button type="button" className="td-action" disabled={isUpdatingStatus} onClick={handleStatus}>
            {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
            {isActive ? "Deactivate" : "Activate"}
          </button>
          <button type="button" className="td-action is-danger" onClick={onDelete}>
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>

      <aside className="td-schedule">
        <div className="td-schedule-head">
          <h3>Issued stock</h3>
          <p>
            {issuances.length} record{issuances.length === 1 ? "" : "s"}
            {" · "}
            {issued} units out
          </p>
        </div>

        {issuances.length === 0 ? (
          <div className="td-empty">
            <Inbox size={22} />
            <span>No stock issued yet</span>
          </div>
        ) : (
          <div className="td-schedule-list">
            {issuances.map((row, index) => (
              <article
                key={row._id}
                className={`td-lesson ${LESSON_TONES[index % LESSON_TONES.length]}`}
              >
                <div className="td-lesson-copy">
                  <h4>{issuanceAssigneeLabel(row)}</h4>
                  <p>
                    {assigneeTypeLabel(row.assignedToType)} · {row.quantity} units
                  </p>
                  <div className="td-lesson-meta">
                    <span>
                      <i>
                        <Calendar size={13} />
                      </i>
                      {formatDate(row.issuedDate)}
                    </span>
                    {row.notes ? (
                      <span>
                        <i>
                          <Hash size={13} />
                        </i>
                        {row.notes}
                      </span>
                    ) : null}
                  </div>
                  <button type="button" className="inv-return-link" onClick={() => handleReturn(row)}>
                    <RotateCcw size={13} />
                    Return to stock
                  </button>
                </div>
                {coverImage ? (
                  <img className="td-lesson-avatar inv-lesson-avatar" src={`${UPLOADS_URL}/${coverImage}`} alt="" />
                ) : (
                  <span className="td-lesson-avatar inv-lesson-avatar">
                    <Package size={16} />
                  </span>
                )}
              </article>
            ))}
          </div>
        )}
      </aside>

      <Drawer
        open={issueOpen}
        onClose={closeIssueDrawer}
        placement="right"
        title="Issue stock"
        width={420}
        footer={
          <>
            <button
              type="button"
              className="bw-drawer-btn is-ghost"
              onClick={closeIssueDrawer}
              disabled={isIssuing}
            >
              Cancel
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={handleIssue} disabled={isIssuing}>
              {isIssuing ? "Issuing..." : "Issue"}
            </button>
          </>
        }
      >
        <p className="inv-issue-hint">{available} units available to issue</p>
        <Form form={form} layout="vertical" className="inv-issue-form">
          <div className="inv-drawer-field">
            <p className="inv-drawer-label">
              Quantity to issue<span>*</span>
            </p>
            <Form.Item
              name="quantity"
              className="inv-form-item"
              rules={[{ required: true, message: "Enter quantity to issue" }]}
            >
              <Input type="number" min={1} max={available} placeholder="e.g. 10" />
            </Form.Item>
          </div>

          <div className="inv-drawer-field">
            <p className="inv-drawer-label">
              Issue to<span>*</span>
            </p>
            <Form.Item name="assignedToType" className="inv-form-item" initialValue="TEACHER">
              <select>
                {ASSIGNED_TO_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Form.Item>
          </div>

          {assignedToType === "TEACHER" ? (
            <div className="inv-drawer-field">
              <p className="inv-drawer-label">
                Teacher<span>*</span>
              </p>
              <Form.Item
                name="assignedToId"
                className="inv-form-item"
                rules={[{ required: true, message: "Select teacher" }]}
              >
                <select>
                  <option value="">Select teacher</option>
                  {teachers.map((teacher) => (
                    <option key={teacher._id} value={teacher._id}>
                      {`${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||
                        teacher.teacherId ||
                        "Teacher"}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </div>
          ) : null}

          {assignedToType === "CLASSROOM" ? (
            <div className="inv-drawer-field">
              <p className="inv-drawer-label">
                Classroom<span>*</span>
              </p>
              <Form.Item
                name="assignedToId"
                className="inv-form-item"
                rules={[{ required: true, message: "Select classroom" }]}
              >
                <select>
                  <option value="">Select classroom</option>
                  {classrooms.map((classroom) => (
                    <option key={classroom._id} value={classroom._id}>
                      {classroom.title || classroom.name || classroom.sectionId || "Classroom"}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </div>
          ) : null}

          {assignedToType === "STAFF" || assignedToType === "DEPARTMENT" ? (
            <div className="inv-drawer-field">
              <p className="inv-drawer-label">
                {assignedToType === "STAFF" ? "Staff name" : "Department name"}
                <span>*</span>
              </p>
              <Form.Item
                name="assignedToName"
                className="inv-form-item"
                rules={[{ required: true, message: "Enter name" }]}
              >
                <Input
                  placeholder={assignedToType === "STAFF" ? "Office admin" : "Science department"}
                />
              </Form.Item>
            </div>
          ) : null}

          <div className="inv-drawer-field">
            <p className="inv-drawer-label">Issue date</p>
            <Form.Item name="issuedDate" className="inv-form-item">
              <DatePicker placeholder="Select date" style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div className="inv-drawer-field">
            <p className="inv-drawer-label">Notes</p>
            <Form.Item name="notes" className="inv-form-item">
              <textarea placeholder="Optional handover notes" rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}
